import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  CombinedFormModal,
  CombinedViewModal,
} from 'components/modals/modules/combined-module/CombinedTransactionModal';
import type {
  CombinedFormData,
  MappingOption,
  NewLotGLFormData,
  ClosingGLOption,
} from 'components/modals/modules/combined-module/CombinedTransactionModal';
import type { CombinedTransaction } from 'api/types/combined.types';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useCombined, useCreateCombined, useUpdateCombined, useSubmitCombined, useDeleteCombined, useLoanMappings, useGoldRates } from 'hooks/api';
import { combinedService } from 'api/services/combinedService';
import { loanMappingService } from 'api/services/loanMappingService';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// EMPTY FORM DATA
// ============================================================================

const createEmptyNewLotGL = (): NewLotGLFormData => ({
  id: `NGL_${Date.now()}`,
  glCustomerId: '',
  glCustomerName: '',
  glNumber: '',
  netWeight: '',
  originalLoanAmount: '',
  outstandingAmount: '',
  loanDate: new Date().toISOString().split('T')[0],
});

const emptyFormData: CombinedFormData = {
  closingMappingId: '',
  closingMappingRef: '',
  closingLoanAccountNumber: '',
  closingCustomerName: '',
  closingCustomerId: '',
  approvedAmount: 0,
  addonOutstandingPrincipal: '',
  addonOutstandingInterest: '',
  closingGLs: [],
  newLotCustomerId: '',
  newLotCustomerName: '',
  newLotLoanType: '',
  newLotAccountNumber: '',
  newLotRequestedAmount: '',
  newLotGLs: [createEmptyNewLotGL()],
};

// ============================================================================
// MAIN SCREEN
// ============================================================================

const CombinedTransactionScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useCombined();
  const { createCombined } = useCreateCombined();
  const { updateCombined } = useUpdateCombined();
  const { submitCombined } = useSubmitCombined();
  const { deleteCombined } = useDeleteCombined();
  const { data: loanMappings } = useLoanMappings();
  const { data: goldRates } = useGoldRates();

  // Data from API
  const records = apiData;

  const activeGoldRateId = useMemo(() => {
    const active = goldRates.find(r => r.isActive);
    return active ? String(active.id) : '';
  }, [goldRates]);

  // Transform loan mappings to MappingOption format
  const mappingOptions: MappingOption[] = useMemo(() =>
    loanMappings.map(m => ({
      id: String(m.id),
      mappingRef: `MAP-${String(m.id).substring(0, 8).toUpperCase()}`,
      customerName: m.customerName,
      customerId: m.customerId,
      loanAccountNumber: '',
      approvedAmount: m.approvedAmount,
      outstandingPrincipal: 0,
      outstandingInterest: 0,
      gls: [],
    })),
    [loanMappings]
  );
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<CombinedTransaction[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<CombinedTransaction | null>(null);
  const [formData, setFormData] = useState<CombinedFormData>(emptyFormData);
  const [statusFilter, setStatusFilter] = useState('');
  // Store row versions for optimistic concurrency during edit
  const [editRowVersions, setEditRowVersions] = useState<{ combined: number; closing: number; addon: number }>({ combined: 0, closing: 0, addon: 0 });

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; variant: 'default' | 'danger'; onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() =>
    records.filter((r) => {
      const matchesSearch = !searchQuery ||
        (r.combinedRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.closingRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.newRequestRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.customerName ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === '' || r.statusCode === statusFilter;
      return matchesSearch && matchesStatus;
    }),
    [records, searchQuery, statusFilter]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData({ ...emptyFormData, newLotGLs: [createEmptyNewLotGL()] });
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(async (record: CombinedTransaction) => {
    try {
      // Fetch the full combined detail from API (includes closing + addon details)
      const response = await combinedService.getById(record.id);
      const detail = response.data as any;
      const closingDetail = detail?.closingDetail;
      const addonDetail = detail?.newRequestDetail;

      // Store row versions for concurrency control
      setEditRowVersions({
        combined: detail?.rowVersion ?? 0,
        closing: closingDetail?.rowVersion ?? 0,
        addon: addonDetail?.rowVersion ?? 0,
      });

      // Get mapping data to merge GLs (like Closing screen does)
      let allClosingGLs: ClosingGLOption[] = [];
      if (closingDetail?.mappingId) {
        try {
          const mappingResponse = await loanMappingService.getById(closingDetail.mappingId);
          const mappingDetail = mappingResponse.data;
          const savedGLs = new Map<string, any>(
            (closingDetail?.glEntries ?? []).map((gl: any) => [gl.mappedGlId || String(gl.id), gl])
          );
          allClosingGLs = (mappingDetail?.mappedGls ?? [])
            .filter((gl: any) => gl.statusCode === 'OPEN')
            .map((gl: any) => {
              const saved = savedGLs.get(String(gl.id));
              return {
                mappedGlId: String(gl.id),
                glNumber: gl.glNumber ?? '',
                customerName: gl.glCustomerName ?? '',
                netWeight: gl.netWeight,
                allocationRatio: gl.allocationRatio,
                selected: !!saved,
                outstandingPrincipal: saved ? String(saved.outstandingPrincipal) : '',
                outstandingInterest: saved ? String(saved.outstandingInterest) : '',
              };
            });
        } catch {
          // Fallback: use saved GLs only
          allClosingGLs = (closingDetail?.glEntries ?? []).map((gl: any) => ({
            mappedGlId: gl.mappedGlId || String(gl.id),
            glNumber: gl.glNumber ?? '',
            customerName: gl.glCustomerName ?? '',
            netWeight: gl.netWeight,
            allocationRatio: gl.allocationRatio,
            selected: true,
            outstandingPrincipal: String(gl.outstandingPrincipal),
            outstandingInterest: String(gl.outstandingInterest),
          }));
        }
      }

      setFormMode('edit');
      setSelectedRecord(record);
      setFormData({
        closingMappingId: closingDetail?.mappingId ? String(closingDetail.mappingId) : '',
        closingMappingRef: closingDetail?.mappingId ? `MAP-${String(closingDetail.mappingId).substring(0, 8).toUpperCase()}` : '',
        closingCustomerName: closingDetail?.customerName ?? record.customerName ?? '',
        closingCustomerId: closingDetail?.customerId ?? record.customerId ?? '',
        closingLoanAccountNumber: closingDetail?.loanAccountNumber ?? record.loanAccountNumber ?? '',
        approvedAmount: closingDetail?.approvedAmount ?? 0,
        addonOutstandingPrincipal: String(closingDetail?.addonOutstandingPrincipal ?? 0),
        addonOutstandingInterest: String(closingDetail?.addonOutstandingInterest ?? 0),
        closingGLs: allClosingGLs,
        newLotCustomerId: addonDetail?.addonCustomerId ? String(addonDetail.addonCustomerId) : '',
        newLotCustomerName: addonDetail?.customerName ?? '',
        newLotLoanType: addonDetail?.addonLoanType ?? '',
        newLotAccountNumber: addonDetail?.addonLoanAccountNumber ?? '',
        newLotRequestedAmount: String(addonDetail?.requestedAmount ?? 0),
        newLotGLs: (addonDetail?.glEntries ?? []).map((gl: any): NewLotGLFormData => ({
          id: gl.id || `NGL_${Date.now()}_${Math.random()}`,
          glCustomerId: gl.customerId ? String(gl.customerId) : '',
          glCustomerName: gl.customerName ?? '',
          glNumber: gl.glNumber ?? '',
          netWeight: String(gl.netWeight ?? 0),
          originalLoanAmount: String(gl.originalLoanAmount ?? 0),
          outstandingAmount: gl.outstandingAmount != null ? String(gl.outstandingAmount) : '',
          loanDate: gl.loanDate ? gl.loanDate.split('T')[0] : '',
        })),
      });
      setFormModalOpen(true);
    } catch {
      showMessage('Failed to load combined transaction details for editing', 'error');
    }
  }, [showMessage]);

  const handleView = useCallback((record: CombinedTransaction) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  // Build the proper API payloads from form data
  const buildClosingGLPayload = (formData: CombinedFormData) => {
    return formData.closingGLs
      .filter((gl) => gl.selected)
      .map((gl) => ({
        mappedGlId: gl.mappedGlId,
        outstandingPrincipal: parseFloat(gl.outstandingPrincipal) || 0,
        outstandingInterest: parseFloat(gl.outstandingInterest) || 0,
      }));
  };

  const buildNewLotGLPayload = (formData: CombinedFormData) => {
    return formData.newLotGLs.map((gl) => ({
      customerId: gl.glCustomerId,
      glNumber: gl.glNumber,
      netWeight: parseFloat(gl.netWeight) || 0,
      originalLoanAmount: parseFloat(gl.originalLoanAmount) || 0,
      outstandingAmount: gl.outstandingAmount ? parseFloat(gl.outstandingAmount) : null,
      loanDate: gl.loanDate,
    }));
  };

  const handleSaveForm = useCallback(() => {
    // Closing leg validation
    if (!formData.closingMappingId) { showMessage('Please search for a loan mapping first', 'error'); return; }
    const selectedClosingGLs = formData.closingGLs.filter((gl) => gl.selected);
    if (selectedClosingGLs.length === 0) { showMessage('Select at least one GL for closing', 'error'); return; }
    const hasEmptyClosingGL = selectedClosingGLs.some((gl) => !gl.outstandingPrincipal || !gl.outstandingInterest);
    if (hasEmptyClosingGL) { showMessage('Enter outstanding amounts for all selected closing GLs', 'error'); return; }
    if (!formData.addonOutstandingPrincipal || parseFloat(formData.addonOutstandingPrincipal) <= 0) { showMessage('Outstanding Principal is required', 'error'); return; }
    if (!formData.addonOutstandingInterest || parseFloat(formData.addonOutstandingInterest) <= 0) { showMessage('Outstanding Interest is required', 'error'); return; }

    // New lot leg validation
    if (!formData.newLotCustomerId) { showMessage('Select a customer for the new lot', 'error'); return; }
    if (!formData.newLotLoanType) { showMessage('Select a loan type for the new lot', 'error'); return; }
    if (!formData.newLotAccountNumber.trim()) { showMessage('Enter an account number for the new lot', 'error'); return; }
    if (!formData.newLotRequestedAmount || parseFloat(formData.newLotRequestedAmount) <= 0) { showMessage('Enter a valid requested amount for the new lot', 'error'); return; }
    const hasEmptyNewGL = formData.newLotGLs.some((gl) => !gl.glCustomerId || !gl.glNumber.trim() || !gl.netWeight || !gl.originalLoanAmount || !gl.loanDate);
    if (hasEmptyNewGL) { showMessage('Complete all GL fields for the new lot', 'error'); return; }

    const action = formMode === 'create' ? 'Create' : 'Save';
    setConfirmDialog({
      open: true,
      title: `${action} Combined Transaction`,
      message: formMode === 'create'
        ? 'Are you sure you want to create this combined transaction as Draft?'
        : 'Are you sure you want to save changes?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (formMode === 'create') {
            await createCombined({
              closingLeg: {
                mappingId: formData.closingMappingId,
                addonOutstandingPrincipal: parseFloat(formData.addonOutstandingPrincipal) || 0,
                addonOutstandingInterest: parseFloat(formData.addonOutstandingInterest) || 0,
                glEntries: buildClosingGLPayload(formData),
              },
              newRequestLeg: {
                addonCustomerId: formData.newLotCustomerId,
                addonLoanType: formData.newLotLoanType,
                addonLoanAccountNumber: formData.newLotAccountNumber,
                requestedAmount: parseFloat(formData.newLotRequestedAmount) || 0,
                goldRateId: activeGoldRateId,
                glEntries: buildNewLotGLPayload(formData),
              },
            });
            showMessage('Combined transaction created as Draft', 'success');
          } else if (selectedRecord) {
            await updateCombined(selectedRecord.id, {
              closingLeg: {
                addonOutstandingPrincipal: parseFloat(formData.addonOutstandingPrincipal) || 0,
                addonOutstandingInterest: parseFloat(formData.addonOutstandingInterest) || 0,
                rowVersion: editRowVersions.closing,
                glEntries: buildClosingGLPayload(formData),
              },
              newRequestLeg: {
                addonCustomerId: formData.newLotCustomerId,
                addonLoanType: formData.newLotLoanType,
                addonLoanAccountNumber: formData.newLotAccountNumber,
                requestedAmount: parseFloat(formData.newLotRequestedAmount) || 0,
                goldRateId: activeGoldRateId,
                rowVersion: editRowVersions.addon,
                glEntries: buildNewLotGLPayload(formData),
              },
              rowVersion: editRowVersions.combined,
            });
            showMessage('Combined transaction updated successfully', 'success');
          }
          await refetch();
          setFormModalOpen(false);
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to save combined transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [formMode, formData, selectedRecord, editRowVersions, activeGoldRateId, showMessage, createCombined, updateCombined, refetch]);

  const handleDelete = useCallback((record: CombinedTransaction) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Combined Transaction',
      message: `Are you sure you want to delete ${record.combinedRef}?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteCombined(record.id);
          await refetch();
          showMessage('Combined transaction deleted successfully', 'success');
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to delete combined transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [showMessage, deleteCombined, refetch]);

  const handleSubmit = useCallback((record: CombinedTransaction) => {
    setConfirmDialog({
      open: true,
      title: 'Submit for Approval',
      message: `Are you sure you want to submit ${record.combinedRef} (Net Cash Flow: ${record.netCashFlow >= 0 ? '+' : ''}${formatCurrency(record.netCashFlow)}) for approval?`,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await submitCombined(record.id);
          await refetch();
          showMessage('Combined transaction submitted for approval', 'success');
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to submit combined transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [showMessage, submitCombined, refetch]);

  const handleDirectSubmit = useCallback(() => {
    // Closing leg validation
    if (!formData.closingMappingId) { showMessage('Please search for a loan mapping first', 'error'); return; }
    const selectedClosingGLs = formData.closingGLs.filter((gl) => gl.selected);
    if (selectedClosingGLs.length === 0) { showMessage('Select at least one GL for closing', 'error'); return; }
    const hasEmptyClosingGL = selectedClosingGLs.some((gl) => !gl.outstandingPrincipal || !gl.outstandingInterest);
    if (hasEmptyClosingGL) { showMessage('Enter outstanding amounts for all selected closing GLs', 'error'); return; }
    if (!formData.addonOutstandingPrincipal || parseFloat(formData.addonOutstandingPrincipal) <= 0) { showMessage('Outstanding Principal is required', 'error'); return; }
    if (!formData.addonOutstandingInterest || parseFloat(formData.addonOutstandingInterest) <= 0) { showMessage('Outstanding Interest is required', 'error'); return; }

    // New lot leg validation
    if (!formData.newLotCustomerId) { showMessage('Select a customer for the new lot', 'error'); return; }
    if (!formData.newLotLoanType) { showMessage('Select a loan type for the new lot', 'error'); return; }
    if (!formData.newLotAccountNumber.trim()) { showMessage('Enter an account number for the new lot', 'error'); return; }
    if (!formData.newLotRequestedAmount || parseFloat(formData.newLotRequestedAmount) <= 0) { showMessage('Enter a valid requested amount for the new lot', 'error'); return; }
    const hasEmptyNewGL = formData.newLotGLs.some((gl) => !gl.glCustomerId || !gl.glNumber.trim() || !gl.netWeight || !gl.originalLoanAmount || !gl.loanDate);
    if (hasEmptyNewGL) { showMessage('Complete all GL fields for the new lot', 'error'); return; }

    setConfirmDialog({
      open: true,
      title: 'Submit Combined Transaction',
      message: 'Are you sure you want to submit this combined transaction for approval?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          let recordId: string;

          if (formMode === 'edit' && selectedRecord) {
            // Update existing, then submit
            await updateCombined(selectedRecord.id, {
              closingLeg: {
                addonOutstandingPrincipal: parseFloat(formData.addonOutstandingPrincipal) || 0,
                addonOutstandingInterest: parseFloat(formData.addonOutstandingInterest) || 0,
                rowVersion: editRowVersions.closing,
                glEntries: buildClosingGLPayload(formData),
              },
              newRequestLeg: {
                addonCustomerId: formData.newLotCustomerId,
                addonLoanType: formData.newLotLoanType,
                addonLoanAccountNumber: formData.newLotAccountNumber,
                requestedAmount: parseFloat(formData.newLotRequestedAmount) || 0,
                goldRateId: activeGoldRateId,
                rowVersion: editRowVersions.addon,
                glEntries: buildNewLotGLPayload(formData),
              },
              rowVersion: editRowVersions.combined,
            });
            recordId = selectedRecord.id;
          } else {
            // Create new, then submit
            const created = await createCombined({
              closingLeg: {
                mappingId: formData.closingMappingId,
                addonOutstandingPrincipal: parseFloat(formData.addonOutstandingPrincipal) || 0,
                addonOutstandingInterest: parseFloat(formData.addonOutstandingInterest) || 0,
                glEntries: buildClosingGLPayload(formData),
              },
              newRequestLeg: {
                addonCustomerId: formData.newLotCustomerId,
                addonLoanType: formData.newLotLoanType,
                addonLoanAccountNumber: formData.newLotAccountNumber,
                requestedAmount: parseFloat(formData.newLotRequestedAmount) || 0,
                goldRateId: activeGoldRateId,
                glEntries: buildNewLotGLPayload(formData),
              },
            });
            recordId = String(created?.id);
          }

          if (recordId) await submitCombined(recordId);
          await refetch();

          showMessage('Combined transaction submitted for approval', 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to submit combined transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [formMode, formData, selectedRecord, editRowVersions, activeGoldRateId, showMessage, createCombined, updateCombined, submitCombined, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: CombinedTransaction): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(row) },
    ];
    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(row) });
      actions.push({ id: 'submit', label: row.statusCode === 'RETURNED' ? 'Resubmit for Approval' : 'Submit for Approval', icon: <IconSystem.check size={18} />, onClick: () => handleSubmit(row) });
    }
    if (row.statusCode === 'DRAFT') {
      actions.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(row), variant: 'danger' as const });
    }
    return actions;
  }, [handleView, handleEdit, handleSubmit, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<CombinedTransaction>[] = useMemo(() => [
    { id: 'combinedRef', header: 'Combined Ref', accessorKey: 'combinedRef', size: 160 },
    { id: 'closingRef', header: 'Closing Ref', accessorKey: 'closingRef', size: 160 },
    { id: 'newRequestRef', header: 'New Request Ref', accessorKey: 'newRequestRef', size: 160 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 140 },
    { id: 'businessUnitName', header: 'Branch', accessorKey: 'businessUnitName', size: 140, cell: ({ getValue }: any) => getValue() || '-' },
    {
      id: 'netCashFlow',
      header: 'Net Cash Flow',
      accessorKey: 'netCashFlow',
      size: 150,
      cell: ({ getValue }: any) => {
        const val = getValue();
        return (
          <span style={{ fontWeight: 700, color: val >= 0 ? '#16a34a' : '#dc2626' }}>
            {val >= 0 ? '+' : ''}{formatCurrency(val)}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 150,
      cell: ({ getValue }: any) => {
        const status = getValue();
        return <span style={getStatusBadgeStyle(status)}>{(status ?? '').replace(/_/g, ' ')}</span>;
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'New Combined', onClick: handleAddNew, tooltip: 'Create New Combined Transaction', variant: 'icon', visible: true },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => showMessage('Export functionality will be available after API integration', 'info'), tooltip: 'Export', variant: 'icon' },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Combined Transactions"
        icon={<IconSystem.copy size={20} />}
        description="Manage combined close & new lot transactions with unified cash flow."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search combined...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && records.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load combined transactions</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && records.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              enableColumnFilters={false}
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage="No combined transactions found"
            />
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'RETURNED', label: 'Returned' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <CombinedFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        mappingOptions={mappingOptions}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onSubmit={handleDirectSubmit}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <CombinedViewModal
        isOpen={viewModalOpen}
        recordId={selectedRecord?.id ?? null}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default CombinedTransactionScreen;
